import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PoweredBy } from "@/components/PoweredBy";
import { InstallAppButton, NotificationBanner } from "@/components/PwaControls";
import { ReportButton } from "@/components/ReportButton";
import { hexToHslTriplet, eur } from "@/lib/format";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Building2, CalendarDays, MessageSquareText, Menu,
  Users, Settings as SettingsIcon, LogOut, Wallet, Shield, Clock, ArrowUpRight, LayoutGrid,
  FileText, Link2, FileBarChart2, ChevronDown, Eye, ArrowLeft, UserCog, LifeBuoy, Gift, Activity,
} from "lucide-react";

const NEON = "#E3FF00";

const hubNav = [
  { label: "Home", icon: LayoutGrid, path: "/home" },
  { label: "Borsellino", icon: Wallet, path: "/messages" },
  { label: "Assistenza", icon: LifeBuoy, path: "/support" },
  { label: "Invita & Guadagna", icon: Gift, path: "/referral", ownerOnly: true },
  { label: "Azienda", icon: SettingsIcon, path: "/settings", ownerOnly: true },
];

const mobileNav = [
  { label: "Home", icon: LayoutGrid, path: "/home" },
  { label: "Borsellino", icon: Wallet, path: "/messages" },
  { label: "Assistenza", icon: LifeBuoy, path: "/support" },
  { label: "Altro", icon: Menu, path: "/more" },
];

export default function Layout({ children }) {
  const { user, org, logout, impersonation, stopImpersonation, loadMe } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const slug = localStorage.getItem("mp_campaign");
    if (!slug || !org || isAdmin) { return; }
    localStorage.removeItem("mp_campaign");
    api.post("/campaigns/claim", { slug }).then(({ data }) => {
      if (!data.already) { toast.success(`Vantaggio evento attivato: ${data.months} mesi gratis di tutti i servizi`); loadMe?.(); }
    }).catch(() => {});
  }, [org?.id, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const root = document.documentElement;
    if (org?.brand_color) {
      const t = hexToHslTriplet(org.brand_color);
      if (t) { root.style.setProperty("--primary", t); root.style.setProperty("--ring", t); }
    } else {
      root.style.removeProperty("--primary"); root.style.removeProperty("--ring");
    }
  }, [org?.brand_color]);

  const active = (p) => loc.pathname === p || (p !== "/dashboard" && loc.pathname.startsWith(p));
  const items = hubNav.filter((i) => !(i.ownerOnly && user?.role !== "owner"));
  const [reportOpen, setReportOpen] = useState(true);
  const openPdf = async (path) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Report non disponibile"); }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="w-64 bg-card border-r border-border flex-col shrink-0 min-h-screen sticky top-0 z-30 hidden md:flex">
        <div className="px-5 py-5 border-b border-border">
          <Link to={isAdmin ? "/admin" : "/home"} className="flex items-center gap-2.5" data-testid="brand-logo">
            <div className="h-9 w-9 rounded-lg grid place-items-center font-display font-bold text-sm text-neutral-900" style={{ backgroundColor: NEON }}>MP</div>
            <div>
              <div className="font-display font-bold text-lg leading-none tracking-tight">Mypasspartu</div>
              <div className="text-[10px] text-muted-foreground mt-0.5 truncate max-w-[150px]">{isAdmin ? "Console Admin" : org?.name}</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {isAdmin ? (
            <>
              <SideLink icon={Shield} label="Console Amministratore" active={loc.pathname === "/admin"} onClick={() => navigate("/admin")} />
              {user?.org_id && <SideLink icon={LayoutGrid} label="I miei servizi" active={loc.pathname === "/home"} onClick={() => navigate("/home")} />}
              <SideLink icon={UserCog} label="Impersona Utente" active={loc.pathname.startsWith("/admin/impersonate")} onClick={() => navigate("/admin/impersonate")} />
              <AdminTicketsLink active={loc.pathname.startsWith("/admin/tickets")} onClick={() => navigate("/admin/tickets")} />
              <SideLink icon={Activity} label="Stato Motore" active={loc.pathname.startsWith("/admin/motor")} onClick={() => navigate("/admin/motor")} />
              <div className="pt-2">
                <button onClick={() => setReportOpen(!reportOpen)} data-testid="nav-report-group" className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-semibold bg-[#E3FF00] text-neutral-900 hover:brightness-95 transition-all">
                  <span className="flex items-center gap-3"><FileBarChart2 className="h-4 w-4" /> Report</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${reportOpen ? "" : "-rotate-90"}`} />
                </button>
                {reportOpen && (
                  <div className="mt-1 ml-3 pl-3 border-l border-border space-y-1">
                    <button onClick={() => openPdf("/admin/report/avanzamento.pdf")} data-testid="nav-report-avanzamento" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-[#E3FF00] text-neutral-900 hover:brightness-95 transition-all text-left"><FileText className="h-4 w-4 shrink-0" /> Report Avanzamento</button>
                    <button onClick={() => openPdf("/admin/report/collegamento.pdf")} data-testid="nav-report-collegamento" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-[#E3FF00] text-neutral-900 hover:brightness-95 transition-all text-left"><Link2 className="h-4 w-4 shrink-0" /> Report Collegamento Home Smart</button>
                  </div>
                )}
              </div>
            </>
          ) : items.map((i) => (
            <SideLink key={i.path} icon={i.icon} label={i.label} path={i.path} active={active(i.path)} onClick={() => navigate(i.path)} />
          ))}
        </nav>
        {!isAdmin && org && (
          <button onClick={() => navigate("/messages")} className="mx-3 mb-2 p-3 rounded-xl bg-primary/5 border border-primary/15 hover:border-primary/30 transition-colors text-left" data-testid="sidebar-wallet">
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> Borsellino</div>
            <div className="font-mono-data font-semibold text-lg mt-0.5">{eur(org.wallet_balance_cents)}</div>
          </button>
        )}
        <div className="px-3 pb-4 space-y-2">
          <InstallAppButton className="w-full" />
          <button onClick={() => { logout(); navigate("/login"); }} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold bg-[#E3FF00] text-neutral-900 hover:brightness-95 transition-all" data-testid="logout-button">
            <LogOut className="h-4 w-4" /> Esci
          </button>
          <PoweredBy className="mt-3 pt-3 border-t border-border" />
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col pb-16 md:pb-0">
        {impersonation?.active && (
          <div className="bg-[#E3FF00] text-neutral-900 px-4 py-2.5 text-sm font-semibold flex flex-wrap items-center justify-between gap-3 no-print" data-testid="impersonation-banner">
            <span className="flex items-center gap-2"><Eye className="h-4 w-4" /> Stai impersonando <strong>{impersonation.org_name || org?.name}</strong> — vista azienda</span>
            <button onClick={async () => { await stopImpersonation(); navigate("/admin"); }} data-testid="stop-impersonation-button" className="inline-flex items-center gap-1 bg-neutral-900 text-[#E3FF00] px-3 py-1.5 rounded-lg font-semibold hover:opacity-90 transition-opacity">
              <ArrowLeft className="h-4 w-4" /> Torna alla console admin
            </button>
          </div>
        )}
        {!isAdmin && org && !org.unlimited && <TrialBanner org={org} onUpgrade={() => navigate("/scegli-servizi")} />}
        <header className="sticky top-0 z-20 bg-card/90 backdrop-blur-md border-b border-border px-4 md:px-6 py-3 flex items-center justify-between no-print">
          <div className="md:hidden flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg grid place-items-center font-display font-bold text-xs text-neutral-900" style={{ backgroundColor: NEON }}>MP</div>
            <span className="font-display font-bold">Mypasspartu</span>
          </div>
          <div className="hidden md:block font-display font-semibold text-foreground capitalize">
            {loc.pathname.replace("/", "").split("/")[0] || "dashboard"}
          </div>
          <div className="flex items-center gap-3">
            {org?.logo_url && <img src={fileUrl(org.logo_url)} alt="logo" className="h-8 w-auto max-w-[120px] object-contain" />}
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium leading-none">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground capitalize">{user?.role === "owner" ? "Proprietario" : user?.role}</div>
            </div>
            <div className="h-9 w-9 rounded-full bg-secondary text-secondary-foreground grid place-items-center font-semibold text-sm">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 min-w-0 px-4 md:px-6 py-5 md:py-7 max-w-[1400px] w-full mx-auto animate-in-up">
          <NotificationBanner />
          {children}
        </main>
        {!isAdmin && org && <ReportButton />}
      </div>

      {/* Mobile bottom nav */}
      {!isAdmin && (
        <nav className="fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-lg border-t border-border px-2 py-2 flex items-center justify-around md:hidden shadow-[0_-4px_20px_rgba(0,0,0,0.06)] no-print">
          {mobileNav.map((i) => {
            const Icon = i.icon; const act = active(i.path);
            return (
              <button key={i.path} onClick={() => navigate(i.path)} data-testid={`mobilenav-${i.path.slice(1)}`}
                className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-colors ${act ? "text-primary" : "text-muted-foreground"}`}>
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{i.label}</span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}

function AdminTicketsLink({ active, onClick }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let on = true;
    const load = () => api.get("/admin/tickets/unread-count").then(({ data }) => { if (on) setCount(data.count || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { on = false; clearInterval(t); };
  }, []);
  return (
    <button onClick={onClick} data-testid="nav-admin-tickets"
      className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold bg-[#E3FF00] text-neutral-900 hover:brightness-95 transition-all ${active ? "ring-2 ring-neutral-900 shadow-sm" : ""}`}>
      <span className="flex items-center gap-3"><LifeBuoy className="h-4 w-4 shrink-0" /> Segnalazioni</span>
      {count > 0 && <span data-testid="admin-tickets-badge" className="min-w-5 h-5 px-1.5 rounded-full bg-neutral-900 text-[#E3FF00] text-xs font-bold grid place-items-center">{count}</span>}
    </button>
  );
}

function SideLink({ icon: Icon, label, active, onClick }) {
  return (
    <button onClick={onClick} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold bg-[#E3FF00] text-neutral-900 hover:brightness-95 transition-all ${active ? "ring-2 ring-neutral-900 shadow-sm" : ""}`}>
      <Icon className="h-4 w-4 shrink-0" /> <span className="truncate">{label}</span>
    </button>
  );
}

function TrialBanner({ org, onUpgrade }) {
  if (org.subscription_active) return null;
  if (!org.trial_active) return null;
  const days = org.trial_days_left ?? 0;
  return (
    <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/15 to-orange-500/10 border-b border-amber-500/20 px-4 py-2.5 text-xs sm:text-sm text-amber-900 dark:text-amber-200 flex flex-wrap items-center justify-between gap-3 no-print">
      <span className="flex items-center gap-2" data-testid="trial-banner-countdown">
        <Clock className="h-4 w-4" />
        Prova gratuita di tutti i servizi — <strong>{days} giorni</strong> rimanenti
        {org.campaign_name && <span className="ml-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-[11px] font-semibold" data-testid="trial-banner-event">Evento {org.campaign_name}</span>}
        {org.activated_via_code && <span className="ml-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-[11px] font-semibold">Codice {org.activated_via_code}</span>}
      </span>
      <button onClick={onUpgrade} data-testid="trial-upgrade-button" className="inline-flex items-center gap-1 font-semibold underline underline-offset-2 hover:opacity-80">
        Sblocca piano completo <ArrowUpRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
